import { Injectable } from '@angular/core';
import { AjaxDataServiceService } from '@admin/core/ajax-data-service.service';
import { UrlConfigContainer } from '@admin/core/url-config';
import { UrlConfigService } from '@hrra/core';
import { PromoEmailTemplateModel } from '../models/promo-email-template-model';
import { TemplateEditSubmitDataModel } from '../models/template-edit-submit-data-model';
import { ActionResultModel } from '@admin/shared/models/action-result-model';
import { PagingFilter } from '@admin/shared/models/paging-filter.model';
import { Observable } from 'rxjs';
import { PagingWrapper } from '@admin/core/models/paging-wrapper';

import { SystemConfigurationModel } from '../models/system-configuration-model.model';
import { SystemConfigurationFilterModel } from '../../shared/models/system-configuration-filter-model';
import { AdminUserModel } from '../../shared/models/admin-user-model';
import { AdminUserFilterModel } from '../../shared/models/admin-user-filter-model';
import { PermissionModel } from '@admin/shared/models/permission-model';
import { PermissionFilterModel } from '../models/admin-permission-filter-model';
import { AdminSubmitDataModel } from '../../shared/models/admin-submit-data-model';
import { AdminEditDataModel } from '../../shared/models/admin-edit-data-model';
import { ServerValidationResult } from '@admin/shared/validation/server-validation-result';
import { ProviderWebsiteModel } from '@admin/shared/models/provider-website-model';
import { ProviderWebsiteLanguageModel } from '@admin/shared/models/provider-website-language-model';
import { StaticContentSubmitDataModel } from '../models/static-content-submit-data-model';
import { RoleModel } from '@admin/shared/models/role-model';
import { StaticContentFilterModel } from '../models/static-content-filter-model';
import { EmailSmsTemplateDataModel } from '../models/email-sms-template-data-model';
import { BannerPositionListViewModel } from '../models/banner-position-list-view-model';
import { BannerListViewModel } from '../models/banner-list-view-model';
import { BannerModel } from '../models/banner-model';
import { BannerPositionViewModel } from '../models/banner-position-view-model';
import { BannerPositionModel } from '../models/banner-position-model';

@Injectable({
  providedIn: 'root',
})
export class SettingsService {
  constructor(
    private ajax: AjaxDataServiceService,
    private urlService: UrlConfigService<UrlConfigContainer>
  ) {}

  public ListEmailTemplates() {
    let url = this.urlService.urlConfig.api.data.settings.templates.list;
    return this.ajax.getDataPaging(url, null, PromoEmailTemplateModel);
  }

  public GetEmailTemplate(id: number) {
    let url = this.urlService.urlConfig.api.data.settings.templates.get;
    return this.ajax.getData(url, { templateId: id }, PromoEmailTemplateModel);
  }

  public CreateEmailTemplate(data: TemplateEditSubmitDataModel) {
    let url = this.urlService.urlConfig.api.data.settings.templates.create;
    return this.ajax.postData(url, data, ActionResultModel);
  }

  public EditEmailTemplate(data: TemplateEditSubmitDataModel) {
    let url = this.urlService.urlConfig.api.data.settings.templates.edit;
    return this.ajax.postData(url, data, ActionResultModel);
  }

  public DeleteEmailTemplate(id: number) {
    let url = this.urlService.urlConfig.api.data.settings.templates.delete;
    return this.ajax.postData(url, { TemplateId: id }, ActionResultModel);
  }

  public ApproveEmailTemplate(id: number) {
    let url = this.urlService.urlConfig.api.data.settings.templates.approve;
    return this.ajax.postData(url, { TemplateId: id }, ActionResultModel);
  }

  public ListSystemConfigurations(
    filter: PagingFilter<SystemConfigurationFilterModel>
  ): Observable<PagingWrapper<SystemConfigurationModel>> {
    let url = this.urlService.urlConfig.api.data.settings.systemConfiguration.list;
    return this.ajax.postDataPaging(
      url,
      filter.toObject(),
      SystemConfigurationModel
    );
  }

  public ListAdminUsers(
    filter: PagingFilter<AdminUserFilterModel>
  ): Observable<PagingWrapper<AdminUserModel>> {
    let url = this.urlService.urlConfig.api.data.settings.adminUsers.list;
    return this.ajax.postDataPaging(url, filter.toObject(), AdminUserModel);
  }

  public GetPermissions(
    filter: PagingFilter<PermissionFilterModel>
  ): Observable<PagingWrapper<PermissionModel>> {
    let url = this.urlService.urlConfig.api.data.settings.adminUsers.getPermissions;
    return this.ajax.postDataPaging(url, filter.toObject(), PermissionModel);
  }

  public GetLimitedOrUnlimitedRole(adminId: number) {
    let url = this.urlService.urlConfig.api.data.settings.adminUsers.getLimitedOrUnlimiterRole;
    return this.ajax.postDataPaging(url, { adminId }, RoleModel);
  }

  public SaveNewAdmin(data: AdminSubmitDataModel) {
    let url = this.urlService.urlConfig.api.data.settings.adminUsers.save;
    return this.ajax.postData(url, data, ActionResultModel);
  }

  public GetAdmin(id: number) {
    let url = this.urlService.urlConfig.api.data.settings.adminUsers.getAdmin;
    return this.ajax.getData(url, { adminId: id }, AdminEditDataModel);
  }

  public UpdateAdmin(data: AdminSubmitDataModel, id: number) {
    let url = this.urlService.urlConfig.api.data.settings.adminUsers.update;
    return this.ajax.postData(
      url,
      data,
      //{ data: data, adminId: id },
      ActionResultModel
    );
  }

  public ValidateEmail(adminId: number, email: string) {
    let url = this.urlService.urlConfig.api.data.settings.adminUsers.validateEmail;
    let data = {
      adminId: adminId,
      email: email,
    };
    return this.ajax.postData(url, data, ServerValidationResult).toPromise();
  }

  public ValidateUsername(adminId: number, username: string) {
    let url = this.urlService.urlConfig.api.data.settings.adminUsers.validateUsername;
    let data = {
      adminId: adminId,
      username: username,
    };
    return this.ajax.postData(url, data, ServerValidationResult).toPromise();
  }

  public GetActiveProviderWebsites() {
    let url = this.urlService.urlConfig.api.data.settings.infoPages.getActiveProviderWebsites;
    return this.ajax.postDataPaging(url, null, ProviderWebsiteModel);
  }

  public GetPrimaryAndAltLanguages() {
    let url = this.urlService.urlConfig.api.data.settings.infoPages.getPrimaryAndAltLanguages;
    return this.ajax.postData(url, null, ProviderWebsiteLanguageModel);
  }

  public GetTitleAndContent(filter: StaticContentFilterModel) {
    let url = this.urlService.urlConfig.api.data.settings.infoPages.getTitleAndContent;
    return this.ajax.postData(url, filter, StaticContentSubmitDataModel);
  }

  public UpdateStaticContent(
    data: StaticContentSubmitDataModel,
    oldDataFilter: StaticContentFilterModel
  ) {
    let url = this.urlService.urlConfig.api.data.settings.infoPages.updateStaticContent;
    return this.ajax.postData(
      url,
      { staticContent: data, oldDataFilter },
      ActionResultModel
    );
  }

  public LoadTemplatesData() {
    let url = this.urlService.urlConfig.api.data.settings.smsEmailTemplates.list;
    return this.ajax.getData(url, null, EmailSmsTemplateDataModel);
  }

  public loadBannerPagePositionList() {
    let url = this.urlService.urlConfig.api.data.settings.banners.bannersManagePage;
    return this.ajax.getData(url, null, BannerPositionListViewModel);
  }

  public loadBannerListByPosition(positionId: number) {
    let url = this.urlService.format(this.urlService.urlConfig.api.data.settings.banners.getBannerList, {id: positionId });
    return this.ajax.getData(
      url,
      { positionId: positionId },
      BannerListViewModel
    );
  }

  public getBanner(id: number, positionId: number) {
    let url = this.urlService.format(this.urlService.urlConfig.api.data.settings.banners.getBanner, {id: id, positionId: positionId });
    return this.ajax.getData(url, { bannerId: id, positionId: positionId }, BannerModel);
  }

  public createBanner(banner: BannerModel) {
    let url = this.urlService.urlConfig.api.data.settings.banners.create;
    return this.ajax.postData(url, banner, ActionResultModel);
  }

  public updateBanner(banner: BannerModel) {
    let url = this.urlService.urlConfig.api.data.settings.banners.edit;
    return this.ajax.postData(url, banner, ActionResultModel);
  }

  public updateBannerStatus(bannerId: number, statusId: number) {
    let url = this.urlService.urlConfig.api.data.settings.banners.updateStatus;

    return this.ajax.postData(
      url,
      { bannerId: bannerId, statusId: statusId },
      ActionResultModel
    );
  }

  public getBannerPosition(id: number) {
    let url = this.urlService.format(this.urlService.urlConfig.api.data.settings.banners.getBannerPosition, { id: id });
    return this.ajax.getData(
      url,
      { bannerPositionId: id },
      BannerPositionModel
    );
  }
}
